import * as go from 'gojs/release/go-module';

import {
  LinkCategory,
  LinkLabelCategory,
  NodeCategory,
} from 'src/containers/ProjectDiagram/constants/categories';
import { UNLINK_DIAGRAM_ICON_WITH_CIRCLE } from 'src/containers/ProjectDiagram/constants/geometries';
import {
  BACKGROUND_LAYERS,
  FOREGROUND_LAYERS,
} from 'src/containers/ProjectDiagram/constants/layers';
import {
  LINK_MIN_LENGTH,
  NODE_SIZE,
} from 'src/containers/ProjectDiagram/constants/styles';
import { createExtendedDiagram } from 'src/containers/ProjectDiagram/templates/extensions/diagram';
import { ExtendedForceDirectedLayout } from 'src/containers/ProjectDiagram/templates/extensions/forceDirectedLayout';
import { ExtendedRelinkingTool } from 'src/containers/ProjectDiagram/templates/extensions/relinkingTool';
import { createMappingTemplate } from 'src/containers/ProjectDiagram/templates/mapping';
import { createMappingLabelTemplate } from 'src/containers/ProjectDiagram/templates/mappingLabel';
import { createModel } from 'src/containers/ProjectDiagram/templates/model';
import { createModelConceptClassTemplate } from 'src/containers/ProjectDiagram/templates/modelConceptClass';
import { createModelConceptHierarchyTemplate } from 'src/containers/ProjectDiagram/templates/modelConceptHierarchy';
import { createModelConceptHierarchyLabelTemplate } from 'src/containers/ProjectDiagram/templates/modelConceptHierarchyLabel';
import { createModelConceptRelationshipTemplate } from 'src/containers/ProjectDiagram/templates/modelConceptRelationship';
import { createModelConceptRelationshipLabelTemplate } from 'src/containers/ProjectDiagram/templates/modelConceptRelationshipLabel';
import { createResourceTemplate } from 'src/containers/ProjectDiagram/templates/resource';
import { Diagram } from 'src/containers/ProjectDiagram/types/diagram';

const $ = go.GraphObject.make;

go.Shape.defineArrowheadGeometry('Unlink', UNLINK_DIAGRAM_ICON_WITH_CIRCLE);

export interface CreateDiagramOptions {
  modelSnapshot?: go.ObjectData;

  showContextMenu: go.HTMLInfo['show'];
  hideContextMenu: go.HTMLInfo['hide'];
  showToolTip: go.HTMLInfo['show'];
  hideToolTip: go.HTMLInfo['hide'];

  diagramChangedSelection: go.DiagramEventHandler;
  handleClick: NonNullable<go.GraphObject['click']>;
  handleDoubleClick: NonNullable<go.GraphObject['doubleClick']>;
}

export const createDiagram = ({
  modelSnapshot,

  showContextMenu,
  hideContextMenu,
  showToolTip,
  hideToolTip,

  diagramChangedSelection,
  handleClick,
  handleDoubleClick,
}: CreateDiagramOptions) => {
  if (process.env.GO_LICENSE_KEY) {
    go.Diagram.licenseKey = process.env.GO_LICENSE_KEY;
  }

  const contextMenu = $(go.HTMLInfo, {
    show: showContextMenu,
    hide: hideContextMenu,
  });

  const toolTip = $(go.HTMLInfo, {
    show: showToolTip,
    hide: hideToolTip,
  });

  const resourceTemplate = createResourceTemplate({
    contextMenu,
    handleClick,
    handleDoubleClick,
    toolTip,
  });
  const mappingTemplate = createMappingTemplate({
    contextMenu,
    handleClick,
    handleDoubleClick,
  });
  const mappingLabelTemplate = createMappingLabelTemplate({
    contextMenu,
    handleClick,
    handleDoubleClick,
    toolTip,
  });

  const modelConceptClassTemplate = createModelConceptClassTemplate({
    contextMenu,
    handleClick,
    handleDoubleClick,
    toolTip,
  });
  const modelConceptRelationshipTemplate =
    createModelConceptRelationshipTemplate({
      contextMenu,
      handleClick,
      handleDoubleClick,
    });
  const modelConceptRelationshipLabelTemplate =
    createModelConceptRelationshipLabelTemplate({
      contextMenu,
      handleClick,
      handleDoubleClick,
      toolTip,
    });
  const modelConceptHierarchyTemplate = createModelConceptHierarchyTemplate({
    contextMenu,
    handleClick,
    handleDoubleClick,
  });
  const modelConceptHierarchyLabelTemplate =
    createModelConceptHierarchyLabelTemplate({
      contextMenu,
      handleClick,
      handleDoubleClick,
      toolTip,
    });

  const nodeTemplateMap = new go.Map<string, go.Node>();
  nodeTemplateMap.add(NodeCategory.RESOURCE, resourceTemplate);
  nodeTemplateMap.add(LinkLabelCategory.MAPPING_LABEL, mappingLabelTemplate);
  nodeTemplateMap.add(
    NodeCategory.MODEL_CONCEPT_CLASS,
    modelConceptClassTemplate
  );
  nodeTemplateMap.add(
    LinkLabelCategory.MODEL_CONCEPT_RELATIONSHIP_LABEL,
    modelConceptRelationshipLabelTemplate
  );
  nodeTemplateMap.add(
    LinkLabelCategory.MODEL_CONCEPT_HIERARCHY_LABEL,
    modelConceptHierarchyLabelTemplate
  );

  const linkTemplateMap = new go.Map<string, go.Link>();
  linkTemplateMap.add(LinkCategory.MAPPING, mappingTemplate);
  linkTemplateMap.add(
    LinkCategory.RELATIONSHIP_MAPPING,
    go.GraphObject.make(go.Link)
  );
  linkTemplateMap.add(
    LinkCategory.MODEL_CONCEPT_RELATIONSHIP,
    modelConceptRelationshipTemplate
  );
  linkTemplateMap.add(
    LinkCategory.MODEL_CONCEPT_HIERARCHY,
    modelConceptHierarchyTemplate
  );

  const model = createModel(modelSnapshot);

  const diagram = createExtendedDiagram({
    allowClipboard: false,
    allowCopy: false,
    allowDelete: false,
    allowDragOut: false,
    allowDrop: false,
    allowGroup: false,
    allowInsert: false,
    allowLink: false,
    allowTextEdit: false,

    click: handleClick,
    doubleClick: handleDoubleClick,

    'undoManager.isEnabled': true, // must be set to allow for model change listening
    // have mouse wheel events zoom in and out instead of scroll up and down
    'toolManager.mouseWheelBehavior': go.ToolManager.WheelZoom,

    'toolManager.toolTipDuration': Infinity,
    'toolManager.hoverDelay': 300,

    // 'undoManager.maxHistoryLength': 0,  // uncomment to disable undo/redo functionality
    model,
    layout: $(ExtendedForceDirectedLayout, {
      defaultElectricalCharge: 500,
      defaultSpringLength: LINK_MIN_LENGTH,
      epsilonDistance: 10,
      isInitial: !modelSnapshot,
      moveLimit: NODE_SIZE / 6,
      randomNumberGenerator: null,
    }),
    padding: 100,
    initialAutoScale: go.Diagram.Uniform,

    contextMenu,
    ChangedSelection: diagramChangedSelection,

    nodeTemplateMap,
    linkTemplateMap,
  }) as Diagram;

  const defaultLayer = diagram.findLayer('');
  if (defaultLayer) {
    BACKGROUND_LAYERS.forEach((name) => {
      diagram.addLayerBefore($(go.Layer, { name }), defaultLayer);
    });
    FOREGROUND_LAYERS.forEach((name) => {
      diagram.addLayerAfter($(go.Layer, { name }), defaultLayer);
    });
  }

  diagram.rebuildParts();

  diagram.toolManager.dragSelectingTool.isPartialInclusion = true;
  diagram.toolManager.draggingTool.dragsLink = true;
  diagram.toolManager.relinkingTool = new ExtendedRelinkingTool();
  diagram.toolManager.relinkingTool.isUnconnectedLinkValid = true;

  diagram.toolManager.draggingTool.doActivate = function doActivate() {
    go.DraggingTool.prototype.doActivate.call(this);

    if (hideToolTip) {
      hideToolTip(diagram, diagram.currentTool);
    }
  };

  return diagram;
};
